import { useState, type FormEvent } from "react";
import Reveal from "@/components/feature/Reveal";
import SectionTitle from "@/components/feature/SectionTitle";
import {
  FORM_SUBMIT_URL,
  FORM_ID,
  TEL_DISPLAY,
  TEL_HREF,
  LINE_URL,
  LINE_QR,
  LINE_COLOR,
  HOURS,
} from "@/constants";

const serviceOptions = [
  "不用品回収",
  "遺品整理",
  "生前整理",
  "空き家整理",
  "引っ越し前後の片付け",
  "片付け＋リフォーム",
  "その他",
];

type Status = "idle" | "submitting" | "success" | "error";

export default function Contact() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    const photo = formData.get("photos");
    if (photo instanceof File && photo.size > 0) {
      formData.set("photos", `写真添付あり: ${photo.name}`);
    } else {
      formData.delete("photos");
    }

    setStatus("submitting");
    setFormError("");

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (typeof value === "string") {
          body.append(key, value);
        }
      });

      const response = await fetch(FORM_SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "送信に失敗しました。お手数ですが、お電話またはLINEでご連絡ください。");
      }
    } catch {
      setStatus("error");
      setFormError("通信エラーが発生しました。時間をおいて再度お試しください。");
    }
  };

  return (
    <section id="contact" className="bg-background-100 py-16 md:py-24">
      <div className="site-container">
        <Reveal>
          <SectionTitle
            upper="CONTACT"
            under="「これってお願いできる？」「これくらいだといくら？」という段階でも大丈夫。まずはお気軽にご連絡ください。"
          >
            お問い合わせ・無料見積もり
          </SectionTitle>
        </Reveal>

        <div className="mt-12 grid gap-6 lg:grid-cols-[0.85fr_1.15fr]">
          <Reveal>
            <div className="space-y-4">
              <a
                href={TEL_HREF}
                className="flex cursor-pointer items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-phone-fill text-2xl text-primary-700" />
                </span>
                <span>
                  <span className="block text-sm font-bold text-foreground-600">お電話でのご相談</span>
                  <span className="block font-heading text-2xl font-black text-foreground-950">{TEL_DISPLAY}</span>
                  <span className="block text-xs text-foreground-500">受付 {HOURS}／年中無休</span>
                </span>
              </a>

              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <div className="flex items-center gap-3">
                  <span
                    className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full"
                    style={{ backgroundColor: LINE_COLOR }}
                  >
                    <i className="ri-line-fill text-2xl text-white" />
                  </span>
                  <div>
                    <p className="block text-sm font-bold text-foreground-600">写真を送るだけのご相談</p>
                    <p className="font-heading text-lg font-black text-foreground-950">LINEで写真相談</p>
                  </div>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  不用品やお部屋の写真を送っていただくと、現地に行かなくてもおおよその内容が分かります。「これもお願いできる？」の確認だけでもOKです。
                </p>
                <a
                  href={LINE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ backgroundColor: LINE_COLOR }}
                  className="mt-3 flex cursor-pointer items-center justify-center gap-2 rounded-md px-5 py-3 font-heading font-black text-white transition-all hover:brightness-95"
                >
                  <i className="ri-line-fill text-lg" />
                  <span className="whitespace-nowrap text-sm">LINEで相談する</span>
                </a>
                <div className="mt-4 flex items-center gap-4 rounded-md bg-background-100 p-4">
                  <div className="h-28 w-28 shrink-0 overflow-hidden rounded-md bg-background-50">
                    <img src={LINE_QR} alt="公式LINEのQRコード" className="h-full w-full object-contain" />
                  </div>
                  <p className="text-sm leading-relaxed text-foreground-600">
                    PCでご覧の方は、スマートフォンのカメラで左のQRコードを読み取ってお友だち追加してください。
                  </p>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={80}>
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <form
                id={FORM_ID}
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="mb-1.5 flex items-center gap-2 text-base font-bold text-foreground-800">
                      お名前
                      <span className="rounded bg-accent-500 px-1.5 py-0.5 text-[10px] font-bold text-background-50">
                        必須
                      </span>
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      placeholder="例：山田 太郎"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-base text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="mb-1.5 flex items-center gap-2 text-base font-bold text-foreground-800">
                      電話番号
                      <span className="rounded bg-accent-500 px-1.5 py-0.5 text-[10px] font-bold text-background-50">
                        必須
                      </span>
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      placeholder="例：090-1234-5678"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-base text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="email" className="mb-1.5 block text-base font-bold text-foreground-800">
                      メールアドレス
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="例：example@mail.com"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-base text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                  <div>
                    <label htmlFor="address" className="mb-1.5 block text-base font-bold text-foreground-800">
                      住所（市区町村程度でも可）
                    </label>
                    <input
                      id="address"
                      name="address"
                      type="text"
                      placeholder="例：春日部市"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-base text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="service" className="mb-1.5 block text-base font-bold text-foreground-800">
                      希望するサービス
                    </label>
                    <select
                      id="service"
                      name="service"
                      defaultValue=""
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-base text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                    >
                      <option value="">選択してください</option>
                      {serviceOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="desired_date" className="mb-1.5 block text-base font-bold text-foreground-800">
                      希望日時
                    </label>
                    <input
                      id="desired_date"
                      name="desired_date"
                      type="text"
                      placeholder="例：9月20日 午前 など"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-base text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="mb-1.5 block text-base font-bold text-foreground-800">
                    相談内容
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    maxLength={500}
                    placeholder="不用品の内容や量、お困りごとなどをご記入ください（500文字以内）"
                    className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-base text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                  />
                </div>

                <div>
                  <label htmlFor="photos" className="mb-1.5 block text-base font-bold text-foreground-800">
                    写真添付（任意）
                  </label>
                  <input
                    id="photos"
                    name="photos"
                    type="file"
                    accept="image/*"
                    multiple
                    className="w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-xs text-foreground-600 file:mr-3 file:cursor-pointer file:rounded file:border-0 file:bg-primary-100 file:px-3 file:py-1.5 file:text-xs file:font-bold file:text-primary-800"
                  />
                  <p className="mt-1.5 flex items-center gap-1.5 text-[11px] text-foreground-500">
                    <i className="ri-information-line" />
                    写真はLINEでお送りいただく方がスムーズです。
                  </p>
                </div>

                {/* 送信補足エリア */}
                <input
                  type="text"
                  name="website_alt"
                  className="form-supplemental-area"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {status === "success" ? (
                  <div className="flex items-center gap-2 rounded-md border border-primary-300 bg-primary-50 px-4 py-3 text-sm font-bold text-primary-800">
                    <i className="ri-checkbox-circle-fill text-lg" />
                    送信が完了しました。内容を確認のうえ、順次ご連絡いたします。
                  </div>
                ) : null}
                {status === "error" ? (
                  <div className="flex items-start gap-2 rounded-md border border-accent-300 bg-accent-50 px-4 py-3 text-sm font-bold text-accent-900">
                    <i className="ri-error-warning-fill mt-0.5 text-lg" />
                    <span>{formError}</span>
                  </div>
                ) : null}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-md bg-primary-600 px-5 py-4 font-heading text-base font-black text-background-50 transition-all hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <i className="ri-send-plane-fill text-lg" />
                  <span className="whitespace-nowrap">
                    {status === "submitting" ? "送信中..." : "この内容で送信する"}
                  </span>
                </button>
                <p className="text-center text-[11px] text-foreground-500">
                  しつこい営業は行いません。お気軽にご相談ください。
                </p>
              </form>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}